// src/hooks/usePermissions.js
import { useState, useEffect, useMemo } from 'react';
import { getLoggedInUser } from '../utils/authHelper';

/**
 * A custom hook to manage and check user permissions.
 * Aligns with utils/permission.js: ADMIN / SUPER_ADMIN bypass.
 */
export const usePermissions = () => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const currentUser = getLoggedInUser();
        setUser(currentUser);
        setIsLoading(false);
    }, []);

    const roleName = useMemo(() => {
        const role = user?.role;
        if (!role) return '';
        if (typeof role === 'string') return role.toUpperCase().replace(/\s+/g, '_');
        return String(role?.name || role?.role_name || '')
            .toUpperCase()
            .replace(/\s+/g, '_');
    }, [user]);

    const isPlatformAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN';

    const permissions = useMemo(() => {
        if (!user || !user.role || !Array.isArray(user.role.permissions)) {
            return new Set();
        }
        const permissionTitles = user.role.permissions
            .map((p) =>
                String(typeof p === 'string' ? p : p?.title || p?.name || '')
                    .trim()
                    .toUpperCase()
            )
            .filter(Boolean);
        return new Set(permissionTitles);
    }, [user]);

    const hasPermission = (permissionName) => {
        if (!permissionName || isLoading) {
            return false;
        }
        if (isPlatformAdmin) {
            return true;
        }
        return permissions.has(String(permissionName).toUpperCase());
    };

    return { user, permissions, hasPermission, isLoading, isPlatformAdmin };
};
